import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { Plus, Calendar, X, Check, XCircle, Users, Clock, Edit, CheckCircle, Trash2, Archive, MapPin, AlertTriangle, Briefcase, CalendarDays } from 'lucide-react';
import { holidayAdminApi, holidayRequestApi, employeeApi, employeePickerToEmployee, blockedDateApi } from '../services/api';
import { HolidayRequest, Employee, CreateHolidayRequestRequest, UpdateHolidayRequestRequest, BlockedDate, CreateBlockedDateRequest, LeaveType, EmployeeLeaveTypeInfo } from '../types';
import { useLocationContext } from '../components/Layout';
import InactiveLocationBanner from '../components/InactiveLocationBanner';
import { formatCompanyDate, formatCompanyDateLocale } from '../utils/companyDateFormat';
import { appConfirm } from '../context/SystemDialogContext';

/** Warn when more than this many people on the same schedule/position are off (pending + approved) on one day. */
const POSITION_LEAVE_WARNING_THRESHOLD = 2;
const SCHEDULE_LEAVE_WARNING_THRESHOLD = 2;

type EmployeeCoverageMeta = {
  positionId: number | null;
  positionTitle: string;
  scheduleIds: number[];
  scheduleNames: string[];
};

const HolidayAdmin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { selectedLocationId, isSelectedLocationInactive, selectedLocation } = useLocationContext();
  const [holidayRequests, setHolidayRequests] = useState<HolidayRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [formData, setFormData] = useState<CreateHolidayRequestRequest>({
    employeeId: 0,
    managerSupervisor: '',
    venueDepartment: '',
    startDate: '',
    endDate: '',
    reason: '',
    status: 'Pending',
  });
  const [saving, setSaving] = useState(false);
  const [openActionsId, setOpenActionsId] = useState<number | null>(null);
  const [actionComment, setActionComment] = useState<string>('');
  const [processingAction, setProcessingAction] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<number | null>(null);
  const [softDeletedIds, setSoftDeletedIds] = useState<number[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [blockedDateForm, setBlockedDateForm] = useState<CreateBlockedDateRequest>({
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [editingBlockedDate, setEditingBlockedDate] = useState<BlockedDate | null>(null);
  const [softDeletedBlockedIds, setSoftDeletedBlockedIds] = useState<number[]>([]);
  const [showBlockedDateForm, setShowBlockedDateForm] = useState(false);
  const [employeeLeaveTypes, setEmployeeLeaveTypes] = useState<EmployeeLeaveTypeInfo[]>([]);
  const [holidayTypes, setHolidayTypes] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<'calendar' | 'table' | 'blocked' | 'coverage'>('calendar');
  const [highlightedRequestId, setHighlightedRequestId] = useState<number | null>(null);
  const pendingScrollRequestId = useRef<number | null>(null);
  const [employeeCoverageMeta, setEmployeeCoverageMeta] = useState<Record<number, EmployeeCoverageMeta>>({});
  const [coverageMonth, setCoverageMonth] = useState<Date>(new Date());
  const [scheduleMorePopup, setScheduleMorePopup] = useState<Date | null>(null);

  const generateDateRange = (startDate: string, endDate: string): string[] => {
    if (!startDate || !endDate) return [];
    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const currentDate = new Date(start);
    while (currentDate <= end) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Employees whose main location matches the selected location (or all when no location selected)
  const employeesFiltered = useMemo(() => {
    if (!selectedLocationId) return employees;
    return employees.filter((e) => e.profile?.mainLocationId === selectedLocationId);
  }, [employees, selectedLocationId]);

  const holidayRequestsFiltered = useMemo(() => {
    if (!selectedLocationId) return holidayRequests;
    return holidayRequests.filter(
      (r) =>
        r.employeeMainLocationId === selectedLocationId ||
        (r.employeeMainLocationId == null &&
          employees.find((e) => e.id === r.employeeId)?.profile?.mainLocationId === selectedLocationId)
    );
  }, [holidayRequests, employees, selectedLocationId]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const viewParam = searchParams.get('view');
    const requestIdParam = searchParams.get('requestId');
    if (viewParam === 'table' || requestIdParam) {
      setViewMode('table');
    }
    if (requestIdParam) {
      const id = parseInt(requestIdParam, 10);
      if (!isNaN(id)) {
        pendingScrollRequestId.current = id;
        setHighlightedRequestId(id);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (loading || viewMode !== 'table' || pendingScrollRequestId.current == null) return;
    const requestId = pendingScrollRequestId.current;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(`holiday-request-${requestId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      pendingScrollRequestId.current = null;
    }, 150);
    const clearHighlight = window.setTimeout(() => {
      setHighlightedRequestId((current) => (current === requestId ? null : current));
    }, 4000);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearHighlight);
    };
  }, [loading, viewMode, holidayRequestsFiltered]);

  useEffect(() => {
    if (formData.employeeId && formData.employeeId > 0) {
      const currentYear = new Date().getFullYear();
      employeeApi.getLeaveTypes(formData.employeeId, currentYear).then((types) => {
        setEmployeeLeaveTypes(types || []);
      }).catch(() => setEmployeeLeaveTypes([]));
    } else {
      setEmployeeLeaveTypes([]);
    }
  }, [formData.employeeId]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const dates = generateDateRange(formData.startDate, formData.endDate);
      setHolidayTypes(prev => {
        const newHolidayTypes: Record<string, string> = {};
        dates.forEach(date => { if (prev[date]) newHolidayTypes[date] = prev[date]; });
        return newHolidayTypes;
      });
    } else {
      setHolidayTypes({});
    }
  }, [formData.startDate, formData.endDate]);

  const fetchData = async (bustCache = false) => {
    try {
      setLoading(true);
      const data = await holidayAdminApi.getBootstrap(bustCache);
      setHolidayRequests(data.holidayRequests);
      setEmployees(data.employees.map(employeePickerToEmployee));
      const meta: Record<number, EmployeeCoverageMeta> = {};
      data.employees.forEach((row) => {
        meta[row.id] = {
          positionId: row.mainPositionId ?? null,
          positionTitle: row.mainPositionTitle?.trim() || 'Unassigned position',
          scheduleIds: (row.scheduleIds || []).filter((id) => id > 0),
          scheduleNames: (row.scheduleNames || []).filter(Boolean),
        };
      });
      setEmployeeCoverageMeta(meta);
      setBlockedDates(data.blockedDates);
      setLeaveTypes(data.leaveTypes);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load holiday requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: 0,
      managerSupervisor: '',
      venueDepartment: '',
      startDate: '',
      endDate: '',
      leaveTypeId: undefined,
      isHalfDay: false,
      halfDayType: undefined,
      totalHours: undefined,
      reason: '',
      status: 'Pending',
    });
    setHolidayTypes({});
    setFormErrors({});
    setEmployeeLeaveTypes([]);
    setIsEditing(false);
    setEditingRequestId(null);
  };

  const isRangeBlocked = (start: string, end: string) => {
    if (!start || !end) return false;
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T00:00:00');

    return blockedDates.some(bd => {
      const bdStart = new Date(bd.startDate + 'T00:00:00');
      const bdEnd = new Date(bd.endDate + 'T00:00:00');

      return (
        (bdStart <= startDate && bdEnd >= startDate) ||
        (bdStart <= endDate && bdEnd >= endDate) ||
        (bdStart >= startDate && bdEnd <= endDate)
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (saving || isSelectedLocationInactive) return;
    
    const errors: Record<string, string> = {};
    if (formData.employeeId === 0) {
      errors.employee = 'Please select an employee.';
    }
    if (!formData.managerSupervisor || formData.managerSupervisor.trim() === '') {
      errors.managerSupervisor = 'Please enter a Manager/Supervisor.';
    }
    if (!formData.venueDepartment || formData.venueDepartment.trim() === '') {
      errors.venueDepartment = 'Please enter a Venue/Department.';
    }
    if (!formData.startDate || !formData.endDate) {
      errors.dates = 'Please select both start and end dates.';
    }
    if (formData.startDate && formData.endDate && isRangeBlocked(formData.startDate, formData.endDate)) {
      errors.dates = 'Selected dates fall within blocked dates and cannot be requested.';
    }
    if (formData.startDate && formData.endDate) {
      const dates = generateDateRange(formData.startDate, formData.endDate);
      const missing = dates.filter(d => !holidayTypes[d] || !holidayTypes[d].trim());
      if (missing.length > 0) {
        errors.holidayTypes = 'Holiday type is required for all days';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      if (errors.employee || errors.managerSupervisor || errors.venueDepartment || errors.dates) {
        alert(Object.values(errors).find(v => v) || 'Please fix the form errors.');
      }
      return;
    }

    try {
      setSaving(true);

      const holidayTypesArray = formData.startDate && formData.endDate
        ? generateDateRange(formData.startDate, formData.endDate)
            .sort()
            .map(date => ({ date, holidayType: holidayTypes[date] || '' }))
        : undefined;

      if (isEditing && editingRequestId != null) {
        const updatePayload: UpdateHolidayRequestRequest = {
          managerSupervisor: formData.managerSupervisor,
          venueDepartment: formData.venueDepartment,
          startDate: formData.startDate,
          endDate: formData.endDate,
          leaveTypeId: formData.leaveTypeId,
          isHalfDay: formData.isHalfDay,
          halfDayType: formData.halfDayType,
          totalHours:
            formData.totalHours != null && formData.totalHours > 0 ? formData.totalHours : undefined,
          reason: formData.reason,
          status: formData.status || 'Pending',
          holidayTypes: holidayTypesArray,
        };

        await holidayRequestApi.update(editingRequestId, updatePayload);
      } else {
        await holidayRequestApi.create({
          ...formData,
          holidayTypes: holidayTypesArray,
        });
      }

      setShowModal(false);
      resetForm();
      await fetchData();
    } catch (error: any) {
      console.error(isEditing ? 'Error updating holiday request:' : 'Error creating holiday request:', error);
      const errorMessage = error?.response?.data?.message || error?.message || `Failed to ${isEditing ? 'update' : 'create'} holiday request. Please try again.`;
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id: number) => {
    if (isSelectedLocationInactive) return;
    if (await appConfirm('Are you sure you want to approve this holiday request?', { confirmLabel: 'Approve' })) {
      try {
        await holidayRequestApi.approve(id);
        await fetchData();
      } catch (error) {
        console.error('Error approving holiday request:', error);
        alert('Failed to approve holiday request. Please try again.');
      }
    }
  };

  const handleReject = async (id: number) => {
    if (isSelectedLocationInactive) return;
    const reason = window.prompt('Please provide a reason for rejection:');
    if (reason !== null) {
      try {
        await holidayRequestApi.reject(id, reason);
        await fetchData();
      } catch (error) {
        console.error('Error rejecting holiday request:', error);
        alert('Failed to reject holiday request. Please try again.');
      }
    }
  };

  const handleApproveWithComment = async (id: number) => {
    if (isSelectedLocationInactive) return;
    if (processingAction) return;
    
    try {
      setProcessingAction(true);
      await holidayRequestApi.approve(id);
      setOpenActionsId(null);
      setActionComment('');
      await fetchData();
    } catch (error) {
      console.error('Error approving holiday request:', error);
      alert('Failed to approve holiday request. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDenyWithComment = async (id: number) => {
    if (isSelectedLocationInactive) return;
    if (processingAction) return;
    
    if (!actionComment.trim()) {
      alert('Please provide a comment before denying the request.');
      return;
    }

    try {
      setProcessingAction(true);
      await holidayRequestApi.reject(id, actionComment);
      setOpenActionsId(null);
      setActionComment('');
      await fetchData();
    } catch (error) {
      console.error('Error denying holiday request:', error);
      alert('Failed to deny holiday request. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (isSelectedLocationInactive) return;
    if (await appConfirm('Are you sure you want to delete this holiday request?', { danger: true, confirmLabel: 'Delete' })) {
      try {
        await holidayRequestApi.delete(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting holiday request:', error);
        alert('Failed to delete holiday request. Please try again.');
      }
    }
  };

  // Calendar rendering
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getHolidayRequestsForDate = (date: Date | null) => {
    if (!date) return [];
    // Normalize date to YYYY-MM-DD format for comparison
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return holidayRequestsFiltered.filter(hr => {
      const start = hr.startDate.split('T')[0]; // Get YYYY-MM-DD part
      const end = hr.endDate.split('T')[0]; // Get YYYY-MM-DD part
      return dateStr >= start && dateStr <= end;
    });
  };

  const getHolidayRequestsForDateAndStatus = (date: Date | null, status: string | null) => {
    const requests = getHolidayRequestsForDate(date);
    if (!status) return requests;
    return requests.filter(hr => hr.status.toLowerCase() === status.toLowerCase());
  };

  const getHolidayCountsForDate = (date: Date | null) => {
    const requests = getHolidayRequestsForDate(date);
    const total = requests.length;
    const pending = requests.filter(hr => hr.status.toLowerCase() === 'pending').length;
    const approved = requests.filter(hr => hr.status.toLowerCase() === 'approved').length;
    const denied = requests.filter(hr => hr.status.toLowerCase() === 'denied').length;
    const cancelled = requests.filter(hr => hr.status.toLowerCase() === 'cancelled').length;
    return { total, pending, approved, denied, cancelled };
  };

  /** Position / schedule breakup for active leave (pending + approved) on a date. */
  const getCoverageBreakupForDate = (date: Date | null) => {
    const requests = getHolidayRequestsForDate(date).filter((hr) => {
      const s = hr.status.toLowerCase();
      return s === 'pending' || s === 'approved';
    });

    const byPosition = new Map<string, { title: string; count: number; pending: number; approved: number; names: string[] }>();
    const bySchedule = new Map<string, { name: string; count: number; pending: number; approved: number; names: string[] }>();

    requests.forEach((hr) => {
      const meta = employeeCoverageMeta[hr.employeeId];
      const empName = `${hr.employee?.firstName || ''} ${hr.employee?.lastName || ''}`.trim() || `Employee #${hr.employeeId}`;
      const status = hr.status.toLowerCase();
      const posKey = meta?.positionId != null ? String(meta.positionId) : 'none';
      const posTitle = meta?.positionTitle || 'Unassigned position';
      const pos = byPosition.get(posKey) || { title: posTitle, count: 0, pending: 0, approved: 0, names: [] };
      pos.count += 1;
      if (status === 'pending') pos.pending += 1;
      if (status === 'approved') pos.approved += 1;
      pos.names.push(empName);
      byPosition.set(posKey, pos);

      // Only count schedules the employee is actually linked to (ignore unassigned).
      const linkedNames = meta?.scheduleNames?.filter(Boolean) ?? [];
      if (linkedNames.length === 0) return;
      linkedNames.forEach((schedName) => {
        const sched = bySchedule.get(schedName) || { name: schedName, count: 0, pending: 0, approved: 0, names: [] };
        sched.count += 1;
        if (status === 'pending') sched.pending += 1;
        if (status === 'approved') sched.approved += 1;
        sched.names.push(empName);
        bySchedule.set(schedName, sched);
      });
    });

    const positions = Array.from(byPosition.values()).sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
    const schedules = Array.from(bySchedule.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    const overThresholdPositions = positions.filter((p) => p.count > POSITION_LEAVE_WARNING_THRESHOLD);
    const overThresholdSchedules = schedules.filter((s) => s.count > SCHEDULE_LEAVE_WARNING_THRESHOLD);
    const overThreshold = [
      ...overThresholdPositions,
      ...overThresholdSchedules.map((s) => ({ title: s.name, count: s.count })),
    ];

    return {
      positions,
      schedules,
      overThreshold,
      overThresholdSchedules,
      overThresholdPositions,
      activeCount: requests.length,
    };
  };

  const handleCountClick = (date: Date, status: string | null) => {
    setSelectedDate(date);
    setSelectedStatus(status);
    setShowDetailsModal(true);
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    // Calculate days: if same date = 1 day, if different dates = difference + 1
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const startFormatted = formatCompanyDate(start);
    const endFormatted = formatCompanyDate(end);
    return `${startFormatted} - ${endFormatted} (${days} ${days === 1 ? 'day' : 'days'})`;
  };

  const getReturnDate = (endDate: string) => {
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);
    return formatCompanyDate(end);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'denied':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
      case 'pending':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentMonth);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-500">Loading holiday requests...</div>
      </div>
    );
  }

  if (!selectedLocationId) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center text-gray-500 max-w-md text-center px-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <MapPin className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Location
          </h2>
          <p className="text-sm">
            Please select a specific location from the dropdown in the top navigation bar to access holiday admin features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">Holiday Admin</h1>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-neutral-300 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-md ${
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm font-medium border-l border-neutral-300 ${
                viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('coverage');
                setCoverageMonth(currentMonth);
              }}
              className={`px-3 py-1.5 text-sm font-medium border-l border-neutral-300 rounded-r-md ${
                viewMode === 'coverage'
                  ? 'bg-blue-600 text-white'
                  : 'text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              Coverage
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setViewMode('blocked');
            }}
            className={`px-3 py-1.5 text-sm font-medium border rounded-md ${
              viewMode === 'blocked'
                ? 'border-red-500 bg-red-600 text-white'
                : 'border-red-300 text-red-700 hover:bg-red-50'
            }`}
          >
            Blocked Dates
          </button>
          {!isSelectedLocationInactive && (
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add New Holiday Request
          </button>
          )}
        </div>
      </div>

      {isSelectedLocationInactive && selectedLocation && (
        <InactiveLocationBanner locationName={selectedLocation.name} />
      )}

      {/* Calendar */}
      {viewMode === 'calendar' && (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-neutral-900">Holiday Calendar - Admin View</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateMonth('prev')}
              className="px-2 py-1 text-neutral-600 hover:bg-neutral-100 rounded-md"
            >
              ←
            </button>
            <h2 className="text-lg font-semibold text-neutral-800">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <button
              onClick={() => navigateMonth('next')}
              className="px-2 py-1 text-neutral-600 hover:bg-neutral-100 rounded-md"
            >
              →
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 rounded-lg overflow-hidden">
          {/* Day headers */}
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-neutral-500 bg-neutral-50 rounded">
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {days.map((date, index) => {
            const counts = getHolidayCountsForDate(date);
            const coverage = date ? getCoverageBreakupForDate(date) : null;
            const isToday = date && date.toDateString() === new Date().toDateString();
            const isBlocked =
              !!date &&
              blockedDates.some(bd => {
                const bdStart = new Date(bd.startDate + 'T00:00:00');
                const bdEnd = new Date(bd.endDate + 'T00:00:00');
                return bdStart <= date && bdEnd >= date;
              });
            
            return (
              <div
                key={index}
                className={`min-h-[100px] p-1 border rounded ${
                  date ? (isBlocked ? 'bg-red-50 border-red-200' : 'bg-white border-black') : 'bg-neutral-50 border-black'
                } ${isToday ? 'ring-1 ring-blue-500' : ''} ${
                  coverage && coverage.overThresholdSchedules.length > 0 && !isBlocked ? 'ring-1 ring-amber-400' : ''
                }`}
              >
                {date && (
                  <>
                    <div className={`text-sm font-medium mb-1 flex items-center justify-between ${isBlocked ? 'text-red-600' : isToday ? 'text-blue-600' : 'text-neutral-900'}`}>
                      <span>{date.getDate()}</span>
                      {coverage && coverage.overThresholdSchedules.length > 0 && !isBlocked && (
                        <span title="Schedule coverage warning">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" aria-hidden />
                        </span>
                      )}
                    </div>
                    {isBlocked ? (
                      <div className="mt-1 text-xs text-red-600 flex flex-col gap-0.5">
                        <span>✕ Blocked</span>
                      </div>
                    ) : (
                      counts.total > 0 && (
                        <div className="space-y-0.5 text-xs">
                          <button
                            onClick={() => handleCountClick(date, null)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline w-full text-left"
                          >
                            <Users className="h-3 w-3" />
                            <span>{counts.total} total</span>
                          </button>
                          {counts.pending > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'pending')}
                              className="flex items-center gap-1 text-orange-600 hover:text-orange-800 hover:underline w-full text-left"
                            >
                              <Clock className="h-3 w-3" />
                              <span>{counts.pending} pending</span>
                            </button>
                          )}
                          {counts.approved > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'approved')}
                              className="flex items-center gap-1 text-green-600 hover:text-green-800 hover:underline w-full text-left"
                            >
                              <Check className="h-3 w-3" />
                              <span>{counts.approved} approved</span>
                            </button>
                          )}
                          {counts.denied > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'denied')}
                              className="flex items-center gap-1 text-red-600 hover:text-red-800 hover:underline w-full text-left"
                            >
                              <XCircle className="h-3 w-3" />
                              <span>{counts.denied} denied</span>
                            </button>
                          )}
                          {counts.cancelled > 0 && (
                            <button
                              onClick={() => handleCountClick(date, 'cancelled')}
                              className="flex items-center gap-1 text-neutral-600 hover:text-neutral-800 hover:underline w-full text-left"
                            >
                              <X className="h-3 w-3" />
                              <span>{counts.cancelled} cancelled</span>
                            </button>
                          )}
                          {coverage && coverage.schedules.slice(0, 2).map((s) => (
                            <button
                              key={s.name}
                              type="button"
                              onClick={() => handleCountClick(date, null)}
                              className={`flex items-center gap-1 w-full text-left hover:underline ${
                                s.count > SCHEDULE_LEAVE_WARNING_THRESHOLD ? 'text-amber-700 font-medium' : 'text-neutral-600'
                              }`}
                              title={s.names.join(', ')}
                            >
                              <CalendarDays className="h-3 w-3 shrink-0" />
                              <span className="truncate">{s.name}: {s.count}</span>
                            </button>
                          ))}
                          {coverage && coverage.schedules.length > 2 && (
                            <button
                              type="button"
                              onClick={() => setScheduleMorePopup(date)}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              More ({coverage.schedules.length - 2})
                            </button>
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Blocked Dates View */}
      {viewMode === 'blocked' && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-neutral-800">Blocked Dates</h2>
            <button
              type="button"
              onClick={() => {
                if (showBlockedDateForm && !editingBlockedDate) {
                  setShowBlockedDateForm(false);
                  setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                } else {
                  setEditingBlockedDate(null);
                  setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                  setShowBlockedDateForm(true);
                }
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              {showBlockedDateForm && !editingBlockedDate ? 'Close Form' : 'Add Blocked Date'}
            </button>
          </div>

          {showBlockedDateForm && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!blockedDateForm.startDate || !blockedDateForm.endDate) {
                  alert('Please select both start and end dates.');
                  return;
                }

                const start = new Date(blockedDateForm.startDate + 'T00:00:00');
                const end = new Date(blockedDateForm.endDate + 'T00:00:00');
                if (end < start) {
                  alert('End date must be after or equal to start date.');
                  return;
                }

                try {
                  if (editingBlockedDate) {
                    await blockedDateApi.update(editingBlockedDate.id, blockedDateForm);
                  } else {
                    await blockedDateApi.create(blockedDateForm);
                  }

                  await fetchData(true);
                  setEditingBlockedDate(null);
                  setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                  setShowBlockedDateForm(false);
                } catch (error: any) {
                  console.error('Error saving blocked date:', error);
                  const errorMessage = error?.response?.data || error?.message || 'Failed to save blocked date.';
                  alert(errorMessage);
                }
              }}
              className="space-y-4 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={blockedDateForm.startDate}
                    onChange={(e) => setBlockedDateForm({ ...blockedDateForm, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    required
                    value={blockedDateForm.endDate}
                    onChange={(e) => setBlockedDateForm({ ...blockedDateForm, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reason <span className="text-xs text-neutral-400">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  value={blockedDateForm.reason || ''}
                  onChange={(e) => setBlockedDateForm({ ...blockedDateForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditingBlockedDate(null);
                    setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
                    setShowBlockedDateForm(false);
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingBlockedDate ? 'Save Changes' : 'Add Blocked Date'}
                </button>
              </div>
            </form>
          )}

          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <div className="bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
              Existing Blocked Dates
            </div>
            {blockedDates.length === 0 ? (
              <div className="px-4 py-3 text-sm text-neutral-500">No blocked dates defined.</div>
            ) : (
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Start Date</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">End Date</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Reason</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {blockedDates.map((bd) => {
                    const isSoftDeleted = softDeletedBlockedIds.includes(bd.id);
                    return (
                      <tr
                        key={bd.id}
                        className={isSoftDeleted ? 'line-through text-neutral-400 bg-neutral-50' : ''}
                      >
                        <td className="px-4 py-2">{formatCompanyDate(bd.startDate)}</td>
                        <td className="px-4 py-2">{formatCompanyDate(bd.endDate)}</td>
                        <td className="px-4 py-2">{bd.reason || 'N/A'}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditingBlockedDate(bd);
                                setBlockedDateForm({
                                  startDate: bd.startDate,
                                  endDate: bd.endDate,
                                  reason: bd.reason || '',
                                });
                                setShowBlockedDateForm(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSoftDeletedBlockedIds(prev =>
                                  prev.includes(bd.id)
                                    ? prev.filter(id => id !== bd.id)
                                    : [...prev, bd.id]
                                );
                              }}
                              className="text-amber-600 hover:text-amber-900"
                              title={isSoftDeleted ? 'Undo soft delete' : 'Soft delete'}
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                            <button
                              onClick={async () => {
                                if (!(await appConfirm('Are you sure you want to delete this blocked date?', { danger: true, confirmLabel: 'Delete' }))) {
                                  return;
                                }
                                try {
                                  await blockedDateApi.delete(bd.id);
                                  await fetchData(true);
                                  setSoftDeletedBlockedIds(prev => prev.filter(id => id !== bd.id));
                                } catch (error: any) {
                                  console.error('Error deleting blocked date:', error);
                                  const errorMessage = error?.response?.data || error?.message || 'Failed to delete blocked date.';
                                  alert(errorMessage);
                                }
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Hard delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Coverage / Position Breakup View */}
      {viewMode === 'coverage' && (() => {
        const covDays = getDaysInMonth(coverageMonth);
        const warningDays = covDays.filter((d): d is Date => !!d).filter((d) => getCoverageBreakupForDate(d).overThreshold.length > 0);
        return (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div>
                <h2 className="text-lg font-semibold text-neutral-800">Schedule / Position Coverage</h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Pending + approved leave by position and schedule. Warning when more than {SCHEDULE_LEAVE_WARNING_THRESHOLD} people on the same schedule (or position) are off on one day.
                  Employees not linked to a schedule are omitted from schedule counts.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCoverageMonth((prev) => { const n = new Date(prev); n.setMonth(prev.getMonth() - 1); return n; })}
                  className="px-2 py-1 text-neutral-600 hover:bg-neutral-100 rounded-md"
                >
                  ←
                </button>
                <span className="min-w-[140px] text-center font-medium">
                  {monthNames[coverageMonth.getMonth()]} {coverageMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={() => setCoverageMonth((prev) => { const n = new Date(prev); n.setMonth(prev.getMonth() + 1); return n; })}
                  className="px-2 py-1 text-neutral-600 hover:bg-neutral-100 rounded-md"
                >
                  →
                </button>
              </div>
            </div>

            {warningDays.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  {warningDays.length} day{warningDays.length === 1 ? '' : 's'} exceed coverage threshold
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {warningDays.slice(0, 8).map((d) => {
                    const cov = getCoverageBreakupForDate(d);
                    return (
                      <li key={d.toISOString()}>
                        <button type="button" className="underline hover:no-underline" onClick={() => handleCountClick(d, null)}>
                          {formatCompanyDateLocale(d, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </button>
                        {': '}
                        {cov.overThreshold.map((p) => `${p.title} (${p.count})`).join(', ')}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="overflow-x-auto border border-neutral-200 rounded-lg">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Date</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Off</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Position breakup</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500">Schedule breakup</th>
                    <th className="px-4 py-2 text-left font-medium text-neutral-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {covDays.filter((d): d is Date => !!d).map((d) => {
                    const cov = getCoverageBreakupForDate(d);
                    if (cov.activeCount === 0) return null;
                    const hasWarn = cov.overThreshold.length > 0;
                    return (
                      <tr key={d.toISOString()} className={hasWarn ? 'bg-amber-50/60' : ''}>
                        <td className="px-4 py-2 whitespace-nowrap font-medium text-neutral-800">
                          {formatCompanyDateLocale(d, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">{cov.activeCount}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {cov.positions.map((p) => (
                              <span
                                key={p.title}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                  p.count > POSITION_LEAVE_WARNING_THRESHOLD
                                    ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                    : 'bg-neutral-100 text-neutral-700'
                                }`}
                                title={p.names.join(', ')}
                              >
                                {p.title}: {p.count}
                                {p.pending > 0 ? ` (${p.pending} pending)` : ''}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {cov.schedules.map((s) => (
                              <span
                                key={s.name}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                  s.count > SCHEDULE_LEAVE_WARNING_THRESHOLD
                                    ? 'bg-amber-100 text-amber-900'
                                    : 'bg-blue-50 text-blue-800'
                                }`}
                                title={s.names.join(', ')}
                              >
                                {s.name}: {s.count}
                              </span>
                            ))}
                            {cov.schedules.length === 0 && (
                              <span className="text-xs text-neutral-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => handleCountClick(d, null)}
                            className="text-blue-600 hover:underline text-xs"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Holiday Requests List */}
      {viewMode === 'table' && (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="p-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">All Holiday Requests</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Leave Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">End Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Days</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Manager/Supervisor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Venue/Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {holidayRequestsFiltered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-neutral-500">
                    No holiday requests found.
                  </td>
                </tr>
              ) : (
                holidayRequestsFiltered.map((request) => {
                  const startDate = new Date(request.startDate);
                  const endDate = new Date(request.endDate);
                  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  const isSoftDeleted = softDeletedIds.includes(request.id);
                  
                  return (
                    <tr
                      key={request.id}
                      id={`holiday-request-${request.id}`}
                      className={`${isSoftDeleted ? 'line-through text-neutral-400 bg-neutral-50' : ''} ${
                        highlightedRequestId === request.id
                          ? 'bg-blue-50 ring-2 ring-inset ring-blue-400'
                          : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.employee?.firstName} {request.employee?.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.leaveType?.name || 'N/A'}
                        {request.isHalfDay && request.halfDayType && (
                          <span className="ml-1 text-xs text-neutral-500">({request.halfDayType} Half Day)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatCompanyDate(startDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatCompanyDate(endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.totalDays || daysDiff} {request.totalDays || daysDiff === 1 ? 'day' : 'days'}
                        {request.isHalfDay && <span className="ml-1 text-xs text-neutral-500">(Half)</span>}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {request.reason || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.managerSupervisor || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.venueDepartment || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const startDateStr = request.startDate.split('T')[0];
                              const endDateStr = request.endDate.split('T')[0];
                              const ht: Record<string, string> = {};
                              if (request.holidayTypes?.length) {
                                request.holidayTypes.forEach(({ date, holidayType }) => { ht[date] = holidayType; });
                              }
                              setFormData({
                                employeeId: request.employeeId,
                                managerSupervisor: request.managerSupervisor,
                                venueDepartment: request.venueDepartment,
                                startDate: startDateStr,
                                endDate: endDateStr,
                                leaveTypeId: request.leaveTypeId,
                                isHalfDay: request.isHalfDay || false,
                                halfDayType: request.halfDayType,
                                totalHours: request.totalHours ?? undefined,
                                reason: request.reason || '',
                                status: request.status,
                              });
                              setHolidayTypes(ht);
                              setFormErrors({});
                              setIsEditing(true);
                              setEditingRequestId(request.id);
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSoftDeletedIds(prev =>
                                prev.includes(request.id)
                                  ? prev.filter(id => id !== request.id)
                                  : [...prev, request.id]
                              );
                            }}
                            className="text-amber-600 hover:text-amber-900"
                            title={isSoftDeleted ? 'Undo soft delete' : 'Soft delete'}
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(request.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Hard delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Add / Edit Holiday Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                {isEditing ? 'Edit Holiday Request' : 'Add New Holiday Request'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Employee *</label>
                <Select<{ value: number; label: string }>
                  value={formData.employeeId ? (() => {
                    const emp = employees.find(e => e.id === formData.employeeId) || employeesFiltered.find(e => e.id === formData.employeeId);
                    return emp ? { value: emp.id, label: `${emp.firstName} ${emp.lastName}` } : { value: formData.employeeId, label: `Employee #${formData.employeeId}` };
                  })() : null}
                  options={employeesFiltered.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                  onChange={(opt: SingleValue<{ value: number; label: string }>) => {
                    const val = opt?.value ?? 0;
                    setFormData({ ...formData, employeeId: val });
                    if (formErrors.employee) setFormErrors({ ...formErrors, employee: '' });
                  }}
                  placeholder="Select employee..."
                  isClearable
                  isDisabled={saving}
                  className={`react-select-container ${formErrors.employee ? 'react-select-error' : ''}`}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: formErrors.employee ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db',
                      minHeight: 38,
                    }),
                  }}
                />
                {formErrors.employee && <p className="mt-1 text-sm text-red-600">{formErrors.employee}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End Date *</label>
                <input
                  type="date"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              {formData.employeeId > 0 && formData.startDate && formData.endDate && generateDateRange(formData.startDate, formData.endDate).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Holiday Type *</label>
                  <p className="text-xs text-neutral-500 mb-2">Select holiday type for each day (availed, remaining shown for selected employee):</p>
                  {formErrors.holidayTypes && <p className="mb-2 text-sm text-red-600">{formErrors.holidayTypes}</p>}
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {generateDateRange(formData.startDate, formData.endDate).map(date => (
                      <div key={date} className="flex items-center gap-3">
                        <span className="text-sm text-neutral-700 min-w-[140px]">
                          {formatCompanyDateLocale(date + 'T00:00:00', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <select
                          value={holidayTypes[date] || ''}
                          onChange={e => {
                            setHolidayTypes({ ...holidayTypes, [date]: e.target.value });
                            if (formErrors.holidayTypes) setFormErrors({ ...formErrors, holidayTypes: '' });
                          }}
                          disabled={saving}
                          className={`flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                            (!holidayTypes[date] || !holidayTypes[date].trim()) && formErrors.holidayTypes
                              ? 'border-red-500' : 'border-neutral-300'
                          }`}
                        >
                          <option value="">Select type...</option>
                          {employeeLeaveTypes.length > 0 ? (
                            employeeLeaveTypes.map(lt => (
                              <option key={lt.leaveTypeId} value={lt.name}>
                                {lt.name} ({lt.takenDays} availed, {lt.pendingDays} pending, {lt.remainingDays} available)
                              </option>
                            ))
                          ) : (
                            <>
                              <option value="Paid Holiday">Paid Holiday</option>
                              <option value="Day Off">Day Off</option>
                            </>
                          )}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isHalfDay || false}
                    onChange={(e) => setFormData({ ...formData, isHalfDay: e.target.checked, halfDayType: e.target.checked ? formData.halfDayType || 'Morning' : undefined })}
                    disabled={saving}
                    className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="text-sm font-medium text-neutral-700">Half Day</span>
                </label>
                {formData.isHalfDay && (
                  <select
                    value={formData.halfDayType || 'Morning'}
                    onChange={(e) => setFormData({ ...formData, halfDayType: e.target.value })}
                    disabled={saving}
                    className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Morning">Morning</option>
                    <option value="Afternoon">Afternoon</option>
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Total hours (optional)</label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={formData.totalHours ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      totalHours: e.target.value === '' ? undefined : parseFloat(e.target.value),
                    })
                  }
                  disabled={saving}
                  placeholder="e.g. 8 for hourly leave"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  For OOH hourly leave: set hours so payroll and balances use hours instead of whole days.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                <select
                  required
                  value={formData.status || 'Pending'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Denied">Denied</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Manager/Supervisor *</label>
                <input
                  type="text"
                  required
                  value={formData.managerSupervisor}
                  onChange={(e) => setFormData({ ...formData, managerSupervisor: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter Manager/Supervisor name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Venue/Department *</label>
                <input
                  type="text"
                  required
                  value={formData.venueDepartment}
                  onChange={(e) => setFormData({ ...formData, venueDepartment: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter Venue/Department name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Comments <span className="text-xs text-neutral-400">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={formData.reason || ''}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  disabled={saving}
                  placeholder="Enter any additional comments (optional)"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (isEditing ? 'Saving...' : 'Creating...') : isEditing ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule coverage More popup */}
      {scheduleMorePopup && (() => {
        const cov = getCoverageBreakupForDate(scheduleMorePopup);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md shadow-xl max-h-[85vh] flex flex-col">
              <div className="flex justify-between items-center border-b border-neutral-200 px-5 py-4 shrink-0">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Schedule leave — {formatCompanyDateLocale(scheduleMorePopup, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </h3>
                <button
                  type="button"
                  onClick={() => setScheduleMorePopup(null)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto space-y-2">
                {cov.schedules.length === 0 ? (
                  <p className="text-sm text-neutral-500">No linked schedule leave for this day.</p>
                ) : (
                  cov.schedules.map((s) => (
                    <div
                      key={s.name}
                      className={`rounded-lg border px-3 py-2 ${
                        s.count > SCHEDULE_LEAVE_WARNING_THRESHOLD
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-neutral-200 bg-neutral-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-neutral-900 inline-flex items-center gap-1.5">
                          <CalendarDays className="h-4 w-4 text-neutral-500" />
                          {s.name}
                        </span>
                        <span className="tabular-nums font-semibold text-neutral-800">{s.count}</span>
                      </div>
                      <div className="mt-1 text-xs text-neutral-600">
                        {s.approved} approved · {s.pending} pending
                      </div>
                      {s.names.length > 0 && (
                        <div className="mt-1 text-xs text-neutral-500 truncate" title={s.names.join(', ')}>
                          {s.names.join(', ')}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="border-t border-neutral-200 px-5 py-3 flex justify-end shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setScheduleMorePopup(null);
                    handleCountClick(scheduleMorePopup, null);
                  }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  View holiday requests
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Holiday Details Modal */}
      {showDetailsModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-neutral-900">
                Holiday Requests for {formatCompanyDateLocale(selectedDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedDate(null);
                  setSelectedStatus(null);
                  setOpenActionsId(null);
                  setActionComment('');
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {(() => {
                const cov = getCoverageBreakupForDate(selectedDate);
                if (cov.activeCount === 0) return null;
                return (
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-neutral-800">Coverage breakup (pending + approved)</h4>
                      {cov.overThreshold.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
                          <AlertTriangle className="h-3 w-3" />
                          Position threshold exceeded
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-neutral-500 mb-1">By position</div>
                      <div className="flex flex-wrap gap-1.5">
                        {cov.positions.map((p) => (
                          <span
                            key={p.title}
                            className={`text-xs px-2 py-1 rounded ${
                              p.count > POSITION_LEAVE_WARNING_THRESHOLD
                                ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                : 'bg-white border border-neutral-200 text-neutral-800'
                            }`}
                            title={p.names.join(', ')}
                          >
                            {p.title}: {p.count} ({p.approved} approved, {p.pending} pending)
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-neutral-500 mb-1">By schedule</div>
                      <div className="flex flex-wrap gap-1.5">
                        {cov.schedules.map((s) => (
                          <span
                            key={s.name}
                            className={`text-xs px-2 py-1 rounded ${
                              s.count > SCHEDULE_LEAVE_WARNING_THRESHOLD
                                ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                : 'bg-white border border-neutral-200 text-neutral-800'
                            }`}
                            title={s.names.join(', ')}
                          >
                            {s.name}: {s.count}
                          </span>
                        ))}
                        {cov.schedules.length === 0 && (
                          <span className="text-xs text-neutral-500">No linked schedules for people off this day.</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {getHolidayRequestsForDateAndStatus(selectedDate, selectedStatus).length === 0 ? (
                <div className="text-center text-neutral-500 py-8">
                  No holiday requests found for this date{selectedStatus ? ` with status "${selectedStatus}"` : ''}.
                </div>
              ) : (
                getHolidayRequestsForDateAndStatus(selectedDate, selectedStatus).map((request) => {
                  const startDate = new Date(request.startDate);
                  const endDate = new Date(request.endDate);
                  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  
                  return (
                    <div key={request.id} className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-lg font-bold text-neutral-900">
                          {request.employee?.firstName} {request.employee?.lastName}
                        </h4>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${getStatusColor(request.status)}`}>
                          {request.status.toLowerCase() === 'approved' && <Check className="h-3 w-3" />}
                          {request.status.toLowerCase() === 'pending' && <Clock className="h-3 w-3" />}
                          {request.status.toLowerCase() === 'denied' && <XCircle className="h-3 w-3" />}
                          {request.status}
                        </span>
                      </div>
                      <div className="text-sm text-neutral-600 mb-4">
                        {formatDateRange(request.startDate, request.endDate)}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Reason</label>
                          <div className="text-sm text-neutral-900">{request.reason || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Department</label>
                          <div className="text-sm text-neutral-900">{request.venueDepartment || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Manager</label>
                          <div className="text-sm text-neutral-900">{request.managerSupervisor || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-500 block mb-1">Return Date</label>
                          <div className="text-sm text-neutral-900">{getReturnDate(request.endDate)}</div>
                        </div>
                      </div>
                      <div className="flex justify-between items-start">
                        <button
                          onClick={() => {
                            const startDateStr = request.startDate.split('T')[0];
                            const endDateStr = request.endDate.split('T')[0];
                            const ht: Record<string, string> = {};
                            if (request.holidayTypes?.length) {
                              request.holidayTypes.forEach(({ date, holidayType }) => { ht[date] = holidayType; });
                            }
                            setFormData({
                              employeeId: request.employeeId,
                              managerSupervisor: request.managerSupervisor,
                              venueDepartment: request.venueDepartment,
                              startDate: startDateStr,
                              endDate: endDateStr,
                              leaveTypeId: request.leaveTypeId,
                              isHalfDay: request.isHalfDay || false,
                              halfDayType: request.halfDayType,
                              totalHours: request.totalHours ?? undefined,
                              reason: request.reason || '',
                              status: request.status,
                            });
                            setHolidayTypes(ht);
                            setFormErrors({});
                            setIsEditing(true);
                            setEditingRequestId(request.id);
                            setShowDetailsModal(false);
                            setSelectedDate(null);
                            setSelectedStatus(null);
                            setOpenActionsId(null);
                            setActionComment('');
                            setShowModal(true);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md border border-neutral-300"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                        {request.status.toLowerCase() === 'pending' && (
                          <button
                            onClick={() => {
                              if (openActionsId === request.id) {
                                setOpenActionsId(null);
                                setActionComment('');
                              } else {
                                setOpenActionsId(request.id);
                                setActionComment('');
                              }
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md border border-neutral-300"
                          >
                            Actions
                          </button>
                        )}
                      </div>
                      {request.status.toLowerCase() === 'pending' && openActionsId === request.id && (
                        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2">
                              Comments
                            </label>
                            <textarea
                              rows={3}
                              value={actionComment}
                              onChange={(e) => setActionComment(e.target.value)}
                              placeholder="Enter comments (required for denial)"
                              disabled={processingAction}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                          <div className="flex gap-3 justify-end">
                            <button
                              onClick={() => handleApproveWithComment(request.id)}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle className="h-4 w-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleDenyWithComment(request.id)}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="h-4 w-4" />
                              Deny
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayAdmin;

